/**
 * ErrorDisplay Component
 *
 * PURPOSE: Display diagnostic information when database errors occur
 * Shows environment info, error details, and possible causes
 */

import { envInfo } from '../lib/supabase';
import type { EnvironmentInfo } from '../lib/env-validator';

interface ErrorDisplayProps {
  error: Error | unknown;
  envInfo?: EnvironmentInfo;
}

export function ErrorDisplay({ error, envInfo: customEnvInfo }: ErrorDisplayProps) {
  const env = customEnvInfo || envInfo;
  const errorMessage = error instanceof Error ? error.message : String(error);

  // Check for common error codes
  const isPGRST205 = errorMessage.includes('PGRST205');
  const is404 = errorMessage.includes('404');
  const isRLSError = errorMessage.includes('row-level security') || errorMessage.includes('permission denied');

  return (
    <div style={{
      maxWidth: '800px',
      margin: '40px auto',
      padding: '24px',
      backgroundColor: '#1a1a1a',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Noto Sans JP", sans-serif',
      color: '#e4e8f0',
    }}>
      <h1 style={{ color: '#e74c3c', marginTop: 0 }}>
        Database Connection Error
      </h1>

      <div style={{
        padding: '16px',
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        borderLeft: '4px solid #e74c3c',
        marginBottom: '24px'
      }}>
        <h3 style={{ marginTop: 0 }}>Error Message:</h3>
        <pre style={{
          overflow: 'auto',
          fontSize: '14px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {errorMessage}
        </pre>
      </div>

      <div style={{
        padding: '16px',
        backgroundColor: '#1a1a1a',
        borderLeft: '4px solid #a0aec0',
        marginBottom: '24px'
      }}>
        <h3 style={{ marginTop: 0 }}>Environment Information:</h3>
        <table style={{ width: '100%', fontSize: '14px' }}>
          <tbody>
            <tr>
              <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>Environment:</td>
              <td style={{ padding: '4px 8px' }}>{env.environment}</td>
            </tr>
            <tr>
              <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>Hostname:</td>
              <td style={{ padding: '4px 8px' }}>{env.hostname}</td>
            </tr>
            <tr>
              <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>Project Ref:</td>
              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                {env.projectRef || 'N/A'}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>Expected Ref:</td>
              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                {env.expectedRef || 'N/A'}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>Validation:</td>
              <td style={{ padding: '4px 8px' }}>
                {env.isValid ? '✅ Valid' : '❌ Invalid'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{
        padding: '16px',
        backgroundColor: 'rgba(255, 152, 0, 0.08)',
        borderLeft: '4px solid #ff9800',
        marginBottom: '24px'
      }}>
        <h3 style={{ marginTop: 0 }}>🔍 Possible Causes:</h3>
        <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
          {isPGRST205 && (
            <li style={{ marginBottom: '8px' }}>
              <strong>PGRST205: Relation not found</strong>
              <br />
              The manual_sections or manual_articles table might not exist in this database.
            </li>
          )}

          {is404 && (
            <li style={{ marginBottom: '8px' }}>
              <strong>404 Not Found</strong>
              <br />
              The table or view might not be created yet.
            </li>
          )}

          {isRLSError && (
            <li style={{ marginBottom: '8px' }}>
              <strong>Row Level Security (RLS) Policy Issue</strong>
              <br />
              The anonymous user might not have permission to read these tables.
            </li>
          )}

          {!env.isValid && env.error && (
            <li style={{ marginBottom: '8px' }}>
              <strong>Environment Validation Failed</strong>
              <br />
              {env.error}
            </li>
          )}

          <li style={{ marginBottom: '8px' }}>
            <strong>Database Schema Not Synced</strong>
            <br />
            The database might be missing migrations for manual_sections and manual_articles tables.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Wrong Database Connection</strong>
            <br />
            You might be connected to a different Supabase project than expected.
          </li>
        </ul>
      </div>

      <div style={{
        padding: '16px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '4px'
      }}>
        <h3 style={{ marginTop: 0 }}>⚠️ Important Note:</h3>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
          This application is <strong>READ-ONLY</strong> and does not modify the database.
          All database changes must be made in the main Telemetry application.
          <br /><br />
          To fix this issue:
          <ol style={{ marginBottom: 0, paddingLeft: '20px' }}>
            <li>Verify that manual_sections and manual_articles tables exist in the database</li>
            <li>Check RLS policies allow anonymous read access</li>
            <li>Ensure environment variables are correctly configured in Vercel</li>
            <li>Confirm project references match between environments</li>
          </ol>
        </p>
      </div>
    </div>
  );
}
